import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

// Phase 1 (Customer Identity, Address & Storefront Modal Reliability) —
// shared accessible modal shell for the public storefront's customer-facing
// dialogs (WelcomeModal, CheckoutModal, VerificationModal). Wraps
// @radix-ui/react-dialog, the same accessible primitive already used on the
// admin side (see components/ui/dialog.jsx), instead of the hand-rolled
// `fixed inset-0` + backdrop-onClick pattern these components used before.
//
// Radix's Dialog.Content gives us — for free, without any hand-built logic —
// a focus trap, an inert/non-interactive background, body scroll lock while
// open, and Escape-to-close. The one behavior Radix does NOT default to what
// this brief requires is backdrop-click-to-dismiss: an accidental tap
// outside the card must never discard a half-filled order. We block that
// explicitly via onPointerDownOutside/onInteractOutside below and leave
// Escape as the only implicit dismiss path, since a deliberate keypress is
// not an "accidental click."
export default function StorefrontModal({
  open = true,
  onCancel,
  onBackdropBlocked,
  testId,
  title,
  description,
  zIndexClassName = "z-40",
  accentClassName = "border-orange-500",
  maxWidthClassName = "max-w-md",
  children,
}) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        // Fires on Escape and on DialogPrimitive.Close activation — never
        // on outside pointer events, which are pre-empted below.
        if (!next) onCancel?.();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={cn("fixed inset-0 bg-black/50", zIndexClassName)} />
        <DialogPrimitive.Content
          data-testid={testId}
          // PR #41 hardening — Phase 4/5. `onBackdropBlocked` is purely
          // observational (safe, masked-only audit event — see
          // lib/customerAuditLog.js); it never affects whether the
          // outside interaction is blocked, which these two handlers
          // already unconditionally guarantee via preventDefault. Wired
          // on onPointerDownOutside only — Radix also invokes
          // onInteractOutside for the SAME pointer event, so hooking
          // both would double-log one accidental click as two events.
          onPointerDownOutside={(e) => { e.preventDefault(); onBackdropBlocked?.(); }}
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            "fixed inset-0 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto focus:outline-none",
            zIndexClassName,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title || "Order"}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {description || "Storefront order dialog"}
          </DialogPrimitive.Description>
          <div
            className={cn(
              "relative w-full bg-white rounded-2xl border-t-4 p-6 text-stone-900 shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto",
              accentClassName,
              maxWidthClassName,
            )}
          >
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
